import React from 'react';
import { Sparkles, X, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BaseballPredictionBar = ({
  selectionCount,
  usageState,
  isLoading,
  error,
  onPredict,
  onClear,
  selectedGames = [],
  isMobile = false
}) => {
  if (selectionCount === 0 && !error) return null;

  const canPredict = usageState.canUse && selectionCount > 0 && !isLoading;
  const totalCost = selectionCount * 7;

  // Desktop version - sleek horizontal bar at the bottom
  if (!isMobile) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 px-6 pb-6"
        >
          <div className="max-w-7xl mx-auto">
            <div className="bg-gradient-to-r from-gray-800/95 via-gray-900/95 to-gray-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-700/50 overflow-hidden">
              {/* Error Banner */}
              {error && (
                <div className="bg-red-500/20 border-b border-red-500/30 px-6 py-3">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={18} className="text-red-300 flex-shrink-0" />
                    <p className="text-sm text-red-200">{error}</p>
                  </div>
                </div>
              )}

              {/* Main Content Area */}
              <div className="px-8 py-5">
                <div className="flex items-center justify-between">
                  {/* Left Side - Selection Info */}
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="absolute inset-0 bg-blue-400/30 rounded-xl blur-lg animate-pulse"></div>
                        <div className="relative p-3 bg-blue-500/20 backdrop-blur-sm rounded-xl border border-blue-400/30">
                          <Sparkles size={24} className="text-blue-300" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">
                          {selectionCount} {selectionCount === 1 ? 'Game Selected' : 'Games Selected'}
                          <span className="text-gray-400 text-sm ml-2">(Max 5)</span>
                        </h3>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-gray-300">
                            Cost: <span className="font-semibold text-yellow-300">{totalCost} coins</span>
                          </span>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-400">
                            7 coins per game
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Progress Indicator */}
                    <div className="hidden lg:block">
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((num) => (
                          <div
                            key={num}
                            className={`w-2.5 h-2.5 rounded-full transition-all ${
                              num <= selectionCount
                                ? 'bg-blue-400 shadow-lg shadow-blue-400/50'
                                : 'bg-gray-700'
                            }`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Selected Games Preview - Desktop Only - Improved compact display */}
                    {selectedGames.length > 0 && (
                      <div className="hidden lg:flex items-center gap-1.5 max-w-md">
                        <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Picks:</span>
                        <div className="flex items-center gap-1 flex-wrap">
                          {selectedGames.slice(0, 4).map((game, index) => {
                            // Get team abbreviations or short names for baseball
                            const homeTeam = game.teams?.home?.code || game.teams?.home?.name?.substring(0, 3).toUpperCase() || 'HOM';
                            const awayTeam = game.teams?.away?.code || game.teams?.away?.name?.substring(0, 3).toUpperCase() || 'AWY';
                            return (
                              <div
                                key={game.fixture?.id || game.game?.id || index}
                                className="text-[10px] bg-black/20 px-1.5 py-0.5 rounded text-gray-400 border border-gray-600/50"
                              >
                                {awayTeam}@{homeTeam}
                              </div>
                            );
                          })}
                          {selectedGames.length > 4 && (
                            <div className="text-[10px] bg-blue-500/20 px-1.5 py-0.5 rounded text-blue-300 border border-blue-500/30 font-bold">
                              +{selectedGames.length - 4}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Side - Actions */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={onClear}
                      className="px-4 py-2.5 bg-black/30 hover:bg-black/40 text-blue-100 rounded-xl transition-all flex items-center gap-2 font-medium border border-blue-500/20"
                      disabled={isLoading}
                    >
                      <X size={18} />
                      <span className="hidden sm:inline">Clear All</span>
                    </button>

                    <button
                      onClick={onPredict}
                      disabled={!canPredict}
                      className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold transition-all transform ${
                        canPredict
                          ? 'bg-gradient-to-r from-blue-400 to-sky-400 text-blue-950 hover:scale-105 shadow-lg hover:shadow-blue-400/30'
                          : 'bg-blue-900/40 text-blue-300/50 cursor-not-allowed'
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Sparkles size={18} />
                          Get AI Predictions
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Insufficient Coins Warning */}
                {!usageState.canUse && selectionCount > 0 && (
                  <div className="mt-4 p-3 bg-yellow-500/20 rounded-xl border border-yellow-500/30">
                    <p className="text-sm text-yellow-200 flex items-center gap-2">
                      <AlertCircle size={16} />
                      Insufficient Star Coins. You need <span className="font-bold">{totalCost}</span> coins to predict these games.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // Mobile version - keep original compact floating style
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50"
      >
        <div className="bg-gray-900/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/10 p-4 min-w-[300px]">
          {/* Error Message */}
          {error && (
            <div className="mb-3 p-3 bg-red-500/10 rounded-lg border border-red-500/20 flex items-center gap-2">
              <AlertCircle size={16} className="text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Main Content */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Sparkles size={20} className="text-blue-300" />
              </div>
              <div>
                <p className="text-white font-bold">
                  {selectionCount} {selectionCount === 1 ? 'Game' : 'Games'} Selected (Max 5)
                </p>
                <p className="text-xs text-white/60">
                  7 coins per game • {selectionCount * 7} coins total
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onClear}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                disabled={isLoading}
              >
                <X size={18} className="text-white/60" />
              </button>

              <button
                onClick={onPredict}
                disabled={!canPredict}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all ${
                  canPredict
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg hover:shadow-xl'
                    : 'bg-white/10 text-white/40 cursor-not-allowed'
                }`}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Predicting...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    Predict Winners
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Usage Warning */}
          {!usageState.canUse && selectionCount > 0 && (
            <div className="mt-3 p-2 bg-yellow-500/10 rounded-lg">
              <p className="text-xs text-yellow-300">
                Insufficient Star Coins. Need {selectionCount * 7} coins.
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BaseballPredictionBar;